import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { AddOptionPopover } from '@/shared/ui';
import { isFilterableAttribute } from '../model/attributeFilters';

/**
 * Основное поле диалога, предлагаемое в перечне наравне с атрибутами: атрибутом оно не является,
 * поэтому строку его фильтра рисует и значение хранит потребитель.
 */
export type AttributeFilterPickerField = {
  /** Код поля — им потребитель узнаёт, какое поле добавлено */
  code: string;
  label: string;
};

type Props = {
  /** Все атрибуты из системной конфигурации */
  availableAttributes: ConversationAttributeItem[];
  /** Пути уже добавленных в панель атрибутов — исключаются из выбора */
  activePaths: ReadonlySet<string>;
  /** Колбэк добавления атрибута по его `path` */
  onAdd: (path: string) => void;
  /** Основные поля диалога, предлагаемые первыми; уже добавленные потребитель не передаёт */
  fields?: AttributeFilterPickerField[];
  /** Колбэк добавления основного поля по его коду */
  onAddField?: (code: string) => void;
};

/** Приставка значения пункта основного поля: путь атрибута с ней не совпадает. */
const FIELD_OPTION_PREFIX = 'field:';

/**
 * Кнопка-поповер для добавления атрибута в активную панель фильтров экрана-потребителя.
 * Атрибуты с `dataType = STRUCTURE` и уже добавленные в панель исключены из списка. Основные поля
 * диалога, переданные потребителем, стоят в том же перечне первыми.
 */
export const AttributeFilterPicker: FC<Props> = ({ availableAttributes, activePaths, onAdd, fields, onAddField }) => {
  const { t } = useTranslation('common');

  const options = useMemo(
    () => [
      ...(fields ?? []).map((field) => ({ value: `${FIELD_OPTION_PREFIX}${field.code}`, label: field.label })),
      ...availableAttributes
        .filter(isFilterableAttribute)
        .filter((a) => !activePaths.has(a.path))
        .map((a) => ({
          value: a.path,
          label: a.pathDescription || a.name || a.path,
        })),
    ],
    [availableAttributes, activePaths, fields],
  );

  const handleAdd = (value: string) => {
    if (value.startsWith(FIELD_OPTION_PREFIX)) {
      onAddField?.(value.slice(FIELD_OPTION_PREFIX.length));
      return;
    }
    onAdd(value);
  };

  return (
    <AddOptionPopover
      label={t('attributeFilters.addAttribute')}
      options={options}
      searchPlaceholder={t('attributeFilters.selectAttribute')}
      emptyMessage={t('attributeFilters.noAttributesFound')}
      onAdd={handleAdd}
    />
  );
};
